import { useState } from "react";
import {
  Alert,
  DialogContent,
  Link,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type {
  EarlierGuest,
  EmailAddress,
  UserId,
} from "@thalamos/haven-common";

import {
  ChoiceTile,
  gutter,
  ShareStepActions,
  useStackedLayout,
} from "./share_step_parts.js";
import type { ShareFailure } from "./ShareWhoStep.js";

/** The value of the tile that says the guest is none of the earlier guests. */
const somebodyNew = "somebody_new";

/**
 * Saying whether the guest is one the address reached before, on a person
 * the sharer reaches, or somebody new, before sharing.
 */
export const ShareEarlierGuestStep = ({
  personName,
  email,
  earlierGuests,
  failure,
  sending,
  onChangeEmail,
  onCancel,
  onShare,
}: {
  /** Who the assessments are about, as a failure names them. */
  personName: string;
  email: EmailAddress;
  earlierGuests: readonly EarlierGuest[];
  failure?: ShareFailure | undefined;
  sending: boolean;
  onChangeEmail: () => Promisable<void>;
  onCancel: () => Promisable<void>;
  /** Shares with the earlier guest picked, or `null` for somebody new. */
  onShare: (earlierGuestUserId: UserId | null) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const { ref, stacked } = useStackedLayout();
  const [picked, setPicked] = useState<UserId | typeof somebodyNew | "">("");

  return (
    <>
      <DialogContent dividers sx={{ px: gutter }}>
        <Stack ref={ref} spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            alignItems="baseline"
            flexWrap="wrap"
          >
            <Typography variant="body1" id="share-guest-earlier">
              {t("shareWithGuest.earlierGuest.usedBefore", { email })}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              disabled={sending}
              onClick={() => {
                void onChangeEmail();
              }}
            >
              {t("shareWithGuest.earlierGuest.change")}
            </Link>
          </Stack>
          <RadioGroup
            aria-labelledby="share-guest-earlier"
            value={picked}
            onChange={(event) => {
              const guest = earlierGuests.find(
                (each) => each.userId === event.target.value,
              );
              setPicked(guest?.userId ?? somebodyNew);
            }}
            sx={{ gap: 1, alignItems: "flex-start" }}
          >
            {earlierGuests.map((guest) => (
              <ChoiceTile
                key={guest.userId}
                value={guest.userId}
                picked={picked === guest.userId}
                disabled={sending}
                label={
                  guest.jobTitle === null
                    ? t("shareWithGuest.earlierGuest.same", {
                        name: guest.name,
                      })
                    : t("shareWithGuest.earlierGuest.sameWithJobTitle", {
                        name: guest.name,
                        jobTitle: guest.jobTitle,
                      })
                }
              />
            ))}
            <ChoiceTile
              value={somebodyNew}
              picked={picked === somebodyNew}
              disabled={sending}
              label={t("shareWithGuest.earlierGuest.somebodyNew")}
            />
          </RadioGroup>
          {failure === undefined ? null : (
            <Alert severity="error">
              {t(`shareWithGuest.outcome.${failure}`, { personName })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <ShareStepActions
        stacked={stacked}
        sending={sending}
        proceedLabel={t("shareWithGuest.who.send")}
        proceedDisabled={picked === ""}
        onCancel={onCancel}
        onProceed={() => {
          void onShare(picked === somebodyNew || picked === "" ? null : picked);
        }}
      />
    </>
  );
};
